import React, { useState, useEffect, useRef } from 'react';
import { __ } from '@wordpress/i18n';
import { uniq } from 'ramda';

// store
import { storeGet } from '../../../../store';

// api
import AssignedCompanyService from '../../../../service/assigned-company-service';

// tools
import set404FromErrorResponse from '../../../../helpers/set404FromErrorResponse';

// components
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import ProperBandoLabel from '../../../../components/ProperBandoLabel';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import getBandoLabel from '../../../../helpers/getBandoLabel';
import getBandoSeverity from '../../../../helpers/getBandoSeverity';
import { Toast } from 'primereact/toast';
import translationStrings from '../../../../translationStringsForComponents';
import { Link } from 'react-router-dom';


const ListCompaniesTablePreInstructor = ({ statuses = ['EVALUATION'] }) => {
    const [items, setItems] = useState(null);
    const [filters, setFilters] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const toast = useRef(null);

    useEffect(() => {
        setLoading(true);

        const userData = storeGet('userData');
        AssignedCompanyService.getAssignedCompanies(getCallback, errGetCallbacks, [
            ['userId', userData.id],
            ['companyStatus', statuses]
        ]);
    }, []);

    const getCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            setItems(getFormattedData(resp.data));
            setStatusOptions(uniq(resp.data.map(o => o.companyStatus)))
            initFilters();
        }
        setLoading(false);
    }

    const errGetCallbacks = (data) => {
        set404FromErrorResponse(data);
        setLoading(false);
    }

    const getFormattedData = (data) => {
        return [...(data || [])].map((d) => {
            d.createdDate = new Date(d.createdDate);
            d.updatedDate = new Date(d.updatedDate);

            return d;
        });
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            companyName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            userName: {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
            },
            companyStatus: {
                operator: FilterOperator.OR,
                constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
            }
        });
    };

    const renderHeader = () => {
        return (
            <div className="appTableHeader">
                <Button type="button" icon="pi pi-filter-slash" label={__('Pulisci', 'htr')} outlined
                        onClick={clearFilter}/>
            </div>
        );
    };

    const dateStartBodyTemplate = (rowData) => {
        return formatDate(rowData.createdDate);
    };

    const dateFilterTemplate = (options) => {
        return <Calendar value={options.value} onChange={(e) => options.filterCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy" placeholder="dd/mm/yyyy" mask="99/99/9999"/>;
    };

    const statusBodyTemplate = (rowData) => {
        return <ProperBandoLabel status={rowData.companyStatus}/>;
    };

    const statusItemTemplate = (option) => {
        return <Tag value={getBandoLabel(option)} severity={getBandoSeverity(option)}/>;
    };

    const statusFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={statusOptions}
                         valueTemplate={options.value ? getBandoLabel(options.value) : null}
                         onChange={(e) => options.filterCallback(e.value, options.index)}
                         itemTemplate={statusItemTemplate} placeholder={__('Filtra per stato', 'htr')}
                         showClear className="p-column-filter"/>;
    };

    const actionsBodyTemplate = (rowData) => {
        const isEvaluated = ['APPROVED', 'REJECTED'].includes(rowData.companyStatus);
        return <div className="appPageSection__tableActions">
            <Link to={`/fornitori/${rowData.companyId}`}>
                {isEvaluated
                    ? <Button severity="secondary" outlined
                              label={__('Mostra', 'htr')}
                              icon="pi pi-eye" size="small"
                              iconPos="right"/>
                    : <Button severity="info"
                              label={__('Valuta', 'htr')}
                              icon="pi pi-arrow-right" size="small"
                              iconPos="right"/>}
            </Link>
        </div>
    }

    const header = renderHeader();

    return (
        <div className="appPageSection__table">
            <Toast ref={toast}/>

            <DataTable value={items} paginator showGridlines rows={5} loading={loading} dataKey="id"
                       filters={filters} stripedRows removableSort
                       header={header}
                       emptyMessage={translationStrings.emptyMessage}
                       onFilter={(e) => setFilters(e.filters)}>
                <Column field="companyId" header={__('ID Reg', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il ID', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="supplierName" header={__('Nome', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca il nome', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column field="companyName" header={__('Azienda', 'htr')}
                        filter sortable
                        filterPlaceholder={__('Cerca la azienda', 'htr')}
                        style={{ minWidth: '8rem' }}/>
                <Column header={__('Invio Documenti', 'htr')} filterField="createdDate" dataType="date"
                        style={{ minWidth: '8rem' }}
                        body={dateStartBodyTemplate} filter filterElement={dateFilterTemplate}/>
                <Column field="companyStatus" header={__('Stato qualifica', 'htr')}
                        filterMenuStyle={{ width: '14rem' }}
                        style={{ minWidth: '7rem' }} body={statusBodyTemplate}
                        filter sortable filterElement={statusFilterTemplate}/>
                <Column header={__('Azioni', 'htr')}
                        body={actionsBodyTemplate}/>
            </DataTable>
        </div>
    )
}

export default ListCompaniesTablePreInstructor;